import { MapPin, Phone, MessageCircle, Instagram, Clock, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useLanguage } from "@/lib/LanguageContext";
import SEOHead from "@/components/SEOHead";
import WorkingHours from "@/components/WorkingHours";

const GIS_LINK = "https://2gis.kz/kyzylorda/geo/70000001062561581/65.494958,44.835179";
const LAT = 44.835179;
const LNG = 65.494958;

export default function ContactsPage() {
  const { t } = useLanguage();

  return (
    <div className="py-24 md:py-32" data-testid="contacts-page">
      <SEOHead path="/contacts" />
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="mb-12">
          <p className="text-sm uppercase tracking-[0.3em] text-primary font-sans mb-2">{t("nav.contacts")}</p>
          <h1 className="text-5xl md:text-6xl font-serif tracking-tight">{t("contacts.title")}</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <div className="flex items-start gap-4" data-testid="contact-address">
              <MapPin className="w-5 h-5 text-primary mt-1 shrink-0" />
              <div>
                <h3 className="font-sans font-semibold mb-1">{t("contacts.address")}</h3>
                <p className="text-muted-foreground text-sm">{t("contacts.addressText")}</p>
                <a href={GIS_LINK} target="_blank" rel="noreferrer" className="text-primary text-xs hover:underline flex items-center gap-1 mt-1">
                  <ExternalLink className="w-3 h-3" /> 2GIS
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4" data-testid="contact-phone">
              <Phone className="w-5 h-5 text-primary mt-1 shrink-0" />
              <div>
                <h3 className="font-sans font-semibold mb-1">{t("contacts.phoneLabel")}</h3>
                <a href="tel:+77027033030" className="text-muted-foreground text-sm hover:text-primary transition-colors">
                  +7 702 703 30 30
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4" data-testid="contact-whatsapp">
              <MessageCircle className="w-5 h-5 text-primary mt-1 shrink-0" />
              <div>
                <h3 className="font-sans font-semibold mb-1">{t("contacts.whatsappLabel")}</h3>
                <a href="https://wa.me/77027033030" target="_blank" rel="noreferrer" className="text-muted-foreground text-sm hover:text-primary transition-colors">
                  +7 702 703 30 30
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4" data-testid="contact-instagram">
              <Instagram className="w-5 h-5 text-primary mt-1 shrink-0" />
              <div>
                <h3 className="font-sans font-semibold mb-1">{t("contacts.instagramLabel")}</h3>
                <a href="https://www.instagram.com/eco_village_kzo" target="_blank" rel="noreferrer" className="text-muted-foreground text-sm hover:text-primary transition-colors">
                  @eco_village_kzo
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4" data-testid="contact-hours">
              <Clock className="w-5 h-5 text-primary mt-1 shrink-0" />
              <div>
                <h3 className="font-sans font-semibold mb-1">{t("contacts.hoursLabel")}</h3>
                <WorkingHours />
              </div>
            </div>

            <div className="pt-4">
              <Link to="/booking" data-testid="contact-booking-btn">
                <Button className="bg-primary text-primary-foreground px-8 py-6 rounded-none uppercase tracking-widest text-sm font-medium hover:bg-primary/90 btn-shine">
                  {t("nav.booking")}
                </Button>
              </Link>
            </div>
          </div>

          {/* 2GIS Map */}
          <div className="flex flex-col gap-3" data-testid="map-section">
            <div className="border border-border overflow-hidden min-h-[400px]">
              <iframe
                title="Eco Village KZO - 2GIS"
                src={`https://widgets.2gis.com/widget?type=firmsonmap&options=%7B%22pos%22%3A%7B%22lat%22%3A${LAT}%2C%22lon%22%3A${LNG}%2C%22zoom%22%3A16%7D%2C%22opt%22%3A%7B%22city%22%3A%22kyzylorda%22%7D%2C%22org%22%3A%2270000001062561581%22%7D`}
                width="100%"
                height="100%"
                style={{ minHeight: "400px", border: "none" }}
                allowFullScreen
              />
            </div>
            <a
              href={GIS_LINK}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 bg-card border border-border py-3 text-sm text-muted-foreground hover:text-primary transition-colors"
              data-testid="open-2gis-btn"
            >
              <ExternalLink className="w-4 h-4" />
              {t("contacts.openIn2GIS")}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
